import React, { useState } from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Grid, 
  TextField, 
  MenuItem, 
  FormControl, 
  FormHelperText,
  Button, 
  Box, 
  Select, 
  SelectChangeEvent,
  InputLabel, 
  Link
} from '@mui/material';
import { useFormContext } from '../context/FormContext';
import { StepProps } from '../types';
import { JOB_TYPES } from '../types/jobTypes';
import states from '../types/states';

const BasicJobInfo: React.FC<StepProps> = ({ onNext }) => {
  const { 
    formData, 
    updateFormData, 
    validationErrors,
    validateStep
  } = useFormContext();
  
  // State to track if we're editing the reporter name
  const [editingReporterName, setEditingReporterName] = useState(false);

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name) {
      updateFormData(name as keyof typeof formData, value);
    }
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    if (name) {
      updateFormData(name as keyof typeof formData, value);
    }
  };

  const handleNext = () => {
    const isValid = validateStep('basic-info');
    if (isValid && onNext) {
      onNext();
    }
  };

  // We're now using the imported JOB_TYPES array and states array

  // Handle reporter selection or editing
  const renderReporterField = () => {
    // Simplified reporter field handling - only label or text input
    
    if (editingReporterName) {
      // When editing, show text input
      return (
        <TextField
          fullWidth
          id="reporterName"
          name="reporterName"
          label="Reporter Name"
          required
          value={formData.reporterName || ''}
          onChange={handleTextChange}
          error={!!validationErrors.reporterName}
          helperText={validationErrors.reporterName}
        />
      );
    } else {
      // Check if reporter name is empty
      if (!formData.reporterName) {
        // Show input field if reporter name is empty
        return (
          <Box>
            <Typography variant="subtitle2" component="div" gutterBottom>
              Reporter Name: <Typography component="span" color="error">*</Typography>
            </Typography>
            <TextField
              fullWidth
              id="reporterName"
              name="reporterName"
              placeholder="Enter your name"
              required
              value=""
              onChange={handleTextChange}
              error={!!validationErrors.reporterName}
              helperText={validationErrors.reporterName || "Please enter your name"}
            />
          </Box>
        );
      }
      
      // Show reporter name with Edit link
      return (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" component="div" gutterBottom>
            Reporter Name: <Typography component="span" color="error">*</Typography>
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body1">{formData.reporterName}</Typography>
            <Link 
              component="button"
              variant="body2"
              onClick={() => setEditingReporterName(true)}
              sx={{ ml: 1 }}
            >
              Edit
            </Link>
          </Box>
        </Box>
      );
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h2" component="h2" gutterBottom>
          Job Information
        </Typography>
        <Typography variant="subtitle1" gutterBottom>
          Please review the pre-populated information and make any necessary changes.
        </Typography>

        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" component="div" gutterBottom>
                Job Number: <Typography component="span" color="error">*</Typography>
              </Typography>
              <Typography variant="body1">{formData.jobNumber}</Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" component="div" gutterBottom>
                Job Date: <Typography component="span" color="error">*</Typography>
              </Typography>
              <Typography variant="body1">
                {formData.jobDate !== "" ? new Date(formData.jobDate + "T12:00:00").toLocaleDateString() : ""}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            {renderReporterField()}
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" component="div" gutterBottom>
                Case Name:
              </Typography>
              <Typography variant="body1">{formData.caseName || 'Not provided'}</Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!validationErrors.jobState}>
              <InputLabel id="job-state-label">Job State *</InputLabel>
              <Select
                labelId="job-state-label"
                id="jobState"
                name="jobState"
                value={formData.jobState || ''}
                onChange={handleSelectChange}
                label="Job State *"
              >
                <MenuItem value="">Select State</MenuItem>
                {states.map(state => (
                  <MenuItem key={state.abbr} value={state.abbr}>{state.name}</MenuItem>
                ))}
              </Select>
              {validationErrors.jobState && (
                <FormHelperText error>{validationErrors.jobState}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!validationErrors.jobType}>
              <InputLabel id="job-type-label">Job Type *</InputLabel>
              <Select
                labelId="job-type-label"
                id="jobType"
                name="jobType"
                value={formData.jobType || ''}
                onChange={handleSelectChange}
                label="Job Type *"
              >
                <MenuItem value="">Select Job Type</MenuItem>
                {JOB_TYPES.map(type => (
                  <MenuItem key={type.code} value={type.code}>{type.name}</MenuItem>
                ))}
              </Select>
              {validationErrors.jobType && (
                <FormHelperText error>{validationErrors.jobType}</FormHelperText>
              )}
            </FormControl>
          </Grid>

        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, pt: 2, borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleNext}
          >
            Continue to Deponents
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default BasicJobInfo;